import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, Eye, CheckCircle2, XCircle, Clock, Loader2,
  ChevronLeft, ChevronRight, MailOpen, MailX, Search
} from 'lucide-react';

const RECIPIENT_TYPES = [
  { value: 'all', label: 'All Recipients' },
  { value: 'merchant', label: 'Merchants' },
  { value: 'partner', label: 'Partners' },
  { value: 'system', label: 'System' },
  { value: 'unknown', label: 'Other' },
];

const TYPE_COLOR = {
  merchant: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  partner: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
  system: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  unknown: 'bg-muted text-muted-foreground border-border',
};

export default function AdminEmailLogs() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterSuccess, setFilterSuccess] = useState('all');
  const [search, setSearch] = useState('');

  const [previewLog, setPreviewLog] = useState(null);
  const [previewBody, setPreviewBody] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const LIMIT = 25;

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (filterType && filterType !== 'all') params.set('recipient_type', filterType);
      if (filterSuccess && filterSuccess !== 'all') params.set('success', filterSuccess);
      const { data } = await axios.get(`${API}/admin/email-logs?${params}`, { headers });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterSuccess]);

  useEffect(() => { setPage(1); fetchLogs(1); }, [filterType, filterSuccess]);
  useEffect(() => { fetchLogs(page); }, [page]);

  const openPreview = async (log) => {
    setPreviewLog(log);
    setPreviewBody('');
    setPreviewLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email-logs/${log.id}`, { headers });
      setPreviewBody(data.body || '');
    } catch {
      toast.error('Failed to load email content');
    } finally {
      setPreviewLoading(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  const filteredLogs = search
    ? logs.filter(l =>
        l.to_email?.toLowerCase().includes(search.toLowerCase()) ||
        l.to_name?.toLowerCase().includes(search.toLowerCase()) ||
        l.subject?.toLowerCase().includes(search.toLowerCase()) ||
        l.event_type?.toLowerCase().includes(search.toLowerCase())
      )
    : logs;

  return (
    <AdminLayout title="Email Logs">
      <div className="space-y-5" data-testid="email-logs-page">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, subject..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 text-sm"
              data-testid="email-logs-search"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-44 text-sm" data-testid="filter-recipient-type">
              <SelectValue placeholder="All Recipients" />
            </SelectTrigger>
            <SelectContent>
              {RECIPIENT_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterSuccess} onValueChange={setFilterSuccess}>
            <SelectTrigger className="w-36 text-sm" data-testid="filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="true">Sent</SelectItem>
              <SelectItem value="false">Failed</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground ml-auto">{total} emails</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Mail className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
              <p className="text-muted-foreground text-sm">No emails logged yet</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="email-logs-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Recipient</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Subject</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Sent</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Status</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Opened</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Read</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredLogs.map(log => (
                      <tr key={log.id} className="hover:bg-muted/20 transition-colors" data-testid={`email-log-row-${log.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground text-[13px] leading-tight">{log.to_name || log.to_email}</p>
                          <p className="text-xs text-muted-foreground">{log.to_email}</p>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <p className="text-[13px] truncate text-foreground" title={log.subject}>{log.subject}</p>
                          <p className="text-[11px] text-muted-foreground">{log.template_slug || log.event_type}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border capitalize ${TYPE_COLOR[log.recipient_type] || TYPE_COLOR.unknown}`}>
                            {log.recipient_type || 'unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[12px] text-muted-foreground whitespace-nowrap">
                          {log.sent_at ? new Date(log.sent_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {log.success ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-400 mx-auto" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-400 mx-auto" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {log.is_opened ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <MailOpen className="h-4 w-4 text-emerald-400" />
                              <span className="text-[10px] text-emerald-400">
                                {log.opened_at ? new Date(log.opened_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : 'Yes'}
                              </span>
                            </div>
                          ) : (
                            <MailX className="h-4 w-4 text-muted-foreground mx-auto opacity-50" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openPreview(log)} data-testid={`view-email-btn-${log.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Email Content Preview Modal */}
      <Dialog open={!!previewLog} onOpenChange={open => !open && setPreviewLog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="email-preview-modal">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              Email Preview
            </DialogTitle>
            {previewLog && (
              <div className="grid grid-cols-2 gap-2 mt-2 text-[12px]">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="font-medium text-foreground">To:</span>
                  {previewLog.to_name !== previewLog.to_email ? `${previewLog.to_name} <${previewLog.to_email}>` : previewLog.to_email}
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="font-medium text-foreground">Date:</span>
                  {previewLog.sent_at ? new Date(previewLog.sent_at).toLocaleString() : '—'}
                </div>
                <div className="col-span-2 flex items-center gap-1.5 text-muted-foreground">
                  <span className="font-medium text-foreground">Subject:</span>
                  {previewLog.subject}
                </div>
                <div className="flex items-center gap-2">
                  {previewLog.success ? (
                    <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20 border">Sent</Badge>
                  ) : (
                    <Badge className="text-[10px] bg-red-500/15 text-red-400 border-red-500/20 border">Failed</Badge>
                  )}
                  {previewLog.is_opened ? (
                    <Badge className="text-[10px] bg-blue-500/15 text-blue-400 border-blue-500/20 border flex items-center gap-1">
                      <MailOpen className="h-2.5 w-2.5" /> Opened {previewLog.opened_at ? new Date(previewLog.opened_at).toLocaleDateString() : ''}
                    </Badge>
                  ) : (
                    <Badge className="text-[10px] bg-muted text-muted-foreground border border-border">Not Opened</Badge>
                  )}
                </div>
              </div>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-auto mt-3 rounded-lg border border-border bg-white min-h-48">
            {previewLoading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : previewBody ? (
              <iframe
                srcDoc={previewBody}
                title="Email Preview"
                className="w-full h-full min-h-[400px] rounded-lg"
                sandbox="allow-same-origin"
                data-testid="email-preview-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
                No content available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
